import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../../components/ui/Modal';
import FormInput from '../../components/ui/FormInput';

const AdminKYCPage: React.FC = () => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'PENDING' | 'APPROVED' | 'REJECTED'>('all');
  const [selectedDoc, setSelectedDoc] = useState<any | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [newStatus, setNewStatus] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [note, setNote] = useState('');

  const load = async () => {
    setLoading(true);
    const res = await adminService.listKYCDocuments(filter === 'all' ? {} : { status: filter });
    setDocuments(res.documents || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [filter]);

  const handleReview = (doc: any) => {
    setSelectedDoc(doc);
    setNewStatus(doc.status === 'APPROVED' ? 'APPROVED' : 'APPROVED');
    setNote('');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!selectedDoc) return;
    await adminService.updateKYCStatus(selectedDoc.id, newStatus, note || undefined);
    setShowModal(false);
    setSelectedDoc(null);
    await load();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">KYC Document Review</h1>

      <div className="flex items-center gap-2 mb-4">
        <select value={filter} onChange={(e) => setFilter(e.target.value as any)} className="px-3 py-2 border rounded">
          <option value="all">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <Button variant="outline" onClick={load}>Refresh</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td className="px-4 py-6" colSpan={5}>Loading…</td></tr>
                ) : documents.length === 0 ? (
                  <tr><td className="px-4 py-6" colSpan={5}>No documents found.</td></tr>
                ) : documents.map(d => (
                  <tr key={d.id}>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{d.user?.email || d.user?.username || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{d.user?.username}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{d.docType}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        d.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                        d.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(d.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-3 space-x-2">
                      <Button size="sm" variant="outline" onClick={() => window.open(d.fileUrl, '_blank')}>View</Button>
                      {d.status === 'PENDING' && (
                        <Button size="sm" onClick={() => handleReview(d)}>Review</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
        <ModalHeader>Review KYC Document</ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value as any)} className="w-full px-3 py-2 border rounded">
                <option value="APPROVED">Approve</option>
                <option value="REJECTED">Reject</option>
              </select>
            </div>
            <FormInput label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            {selectedDoc && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Document:</p>
                <a href={selectedDoc.fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">View Document</a>
              </div>
            )}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={handleSubmit}>Submit</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AdminKYCPage;


